import { prop } from 'ramda';
import { log } from 'log';
import { useEffect, useState } from 'react';
import { useUserSelector } from 'js/store/hooks';

import * as helpers from '../helpers';
import { validateGeolocation } from 'js/hooks/auth/pas';
import * as Types from 'js/components/tagTypes/Geofencing/types';

export const GEOFENCING_MAX_RETRY_TIMES = 5;

export const useScheduler = ({
    sdk,
    nextTime,
    license,
    transactionID,
    userId,
    setTransactionID,
    setLicense,
    setNextTime,
    triggerGeolocation,
    requestPacket,
    geolocationType
}: {
    sdk: Types.GeofencingSdkInstance | null;
    nextTime: number;
    license: string | null;
    transactionID: string | null;
    userId: string | null;
    setTransactionID: (transactionID: string | null) => void;
    setLicense: (license: string | null) => void;
    setNextTime: (nextTime: number) => void;
    geolocationType: string;
    triggerGeolocation: () => Promise<void>;
    requestPacket: () => Promise<void>;
}) => {
    const isAlive = useUserSelector(prop('isAlive'));
    const [retry, setRetry] = useState(0);
    const [needsRetrigger, setNeedsRetrigger] = useState(false);

    /**
     * In case needsRetrigger is true and player is back -- run geolocation flow again.
     */
    useEffect(() => {
        if (isAlive && needsRetrigger) {
            setNeedsRetrigger(false);
            triggerGeolocation();
        }
    }, [
        isAlive,
        needsRetrigger,
        triggerGeolocation
    ]);

    useEffect(() => {
        if (!sdk || !userId) {
            return;
        }

        if (!isAlive) {
            // user is dead
            // therefore we need to retrigger when he becomes alive
            setNeedsRetrigger(true);
            return;
        }

        if (!license || !transactionID) {
            return;
        }

        const timeout = setTimeout(() => {
            const handlerUUID = helpers.retrieveHandlerUUID();
            const transactionHandlerUUID = helpers.retrieveTransactionHandlerUUID(transactionID);

            if (!transactionHandlerUUID) {
                log('gcInfo', {
                    transactionID,
                    handlerUUID,
                    event: 'regular request'
                });

                setRetry(0);

                // we are the first to do it
                helpers.setTransactionHandlerUUID(transactionID);
            } else if (transactionHandlerUUID === handlerUUID) {
                log('gcInfo', {
                    transactionID,
                    handlerUUID,
                    event: 'retry request'
                });

                setRetry(0);
            } else {
                log('gcInfo', {
                    transactionID,
                    handlerUUID,
                    retry,
                    transactionHandlerUUID,
                    event: 'abort request'
                });

                if (retry > GEOFENCING_MAX_RETRY_TIMES) {
                    setRetry(0);

                    // cleanup the block and retry as if no one else is trying
                    helpers.unsetTransactionHandlerUUID(transactionID);
                } else {
                    setRetry(retry + 1);
                }

                // unset current timeout
                setTransactionID(null);
                setLicense(null);
                setNextTime(0);
            }

            requestPacket();
        }, nextTime);

        return () => {
            // unset current timeout
            setTransactionID(null);
            setLicense(null);
            setNextTime(0);
            setRetry(0);
            clearTimeout(timeout);
        };
    }, [
        sdk,
        retry,
        nextTime,
        license,
        isAlive,
        transactionID,
        userId,
        triggerGeolocation,
        setTransactionID,
        setLicense,
        setNextTime,
        requestPacket
    ]);

    useEffect(() => {
        if (!sdk || !userId || retry <= 0 || license || transactionID) {
            return;
        }

        const timeout = setTimeout(() => {
            triggerGeolocation();
        }, 3000 * retry);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        sdk,
        triggerGeolocation,
        license,
        userId,
        transactionID,
        retry
    ]);

    /**
     * Each 20 seconds make a request to IMS in order to trigger IP change, if IP of the player has changed.
     */
    useEffect(() => {
        if (!sdk || !userId || !license || !transactionID || !nextTime) {
            return;
        }

        const interval = setInterval(() => {
            validateGeolocation(null, null, null, null, null, geolocationType);
        }, 20000);

        return () => {
            clearInterval(interval);
        };
    }, [
        sdk,
        userId,
        license,
        transactionID,
        nextTime,
        geolocationType
    ]);
};
